import { useEffect, useRef } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  createFormField,
  FormFieldBlueprint,
} from '@backstage/plugin-scaffolder-react/alpha';
import { microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { TextField } from '@material-ui/core';

// Custom scaffolder fields that pre-fill from the signed-in user's Microsoft
// profile (the same data the Settings page shows — no catalog User entity or
// Directory.Read.All needed). Used via `ui:field:` on the template's
// ownerName / ownerEmail properties; the user can still edit the value.
//
// NOTE: FormFieldBlueprint.make() MUST get a distinct `name` — the extension
// ID is derived from it, and two unnamed field extensions collide with
// "provided duplicate extensions", crashing the whole app at boot.
//
// FILL TIMING: RJSF applies the schema's `default: ''` asynchronously and can
// wipe an early fill; if that leaves formData at '' (the value it started
// at), React never re-renders and a formData-dependent effect never re-runs,
// so the field stays empty forever (the ownerName bug observed with v5). The
// effect therefore schedules its own retries while the field is still empty.
// `userEditedRef` is set by real input events only, so a user deliberately
// editing/clearing the field keeps their value.
const RETRY_DELAYS_MS = [0, 400, 1000, 2000];

const makeProfileField = (pick: 'displayName' | 'email') =>
  createFormField({
    name: pick === 'displayName' ? 'OwnerProfileName' : 'OwnerProfileEmail',
    component: ({
      id,
      name,
      formData,
      onChange,
      required,
      disabled,
      schema,
    }) => {
      const auth = useApi(microsoftAuthApiRef);
      const userEditedRef = useRef(false);
      useEffect(() => {
        if (formData || userEditedRef.current) return;
        const timers = RETRY_DELAYS_MS.map(ms =>
          setTimeout(() => {
            if (userEditedRef.current) return;
            auth
              .getProfile()
              .then(profile => {
                const value = profile?.[pick];
                if (value && !userEditedRef.current) {
                  onChange(value as never);
                }
              })
              .catch(() => undefined);
          }, ms),
        );
        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [formData]);
      return (
        <TextField
          id={id}
          name={name}
          label={schema.title}
          value={(formData as string) ?? ''}
          onChange={e => {
            userEditedRef.current = true;
            onChange(e.target.value as never);
          }}
          required={required}
          disabled={disabled}
          fullWidth
          margin="normal"
          helperText={(schema.description as string) || undefined}
        />
      );
    },
  });

export const ownerProfileFieldsModule = createFrontendModule({
  pluginId: 'scaffolder',
  extensions: [
    FormFieldBlueprint.make({
      name: 'owner-profile-name',
      params: { field: async () => makeProfileField('displayName') },
    }),
    FormFieldBlueprint.make({
      name: 'owner-profile-email',
      params: { field: async () => makeProfileField('email') },
    }),
  ],
});
